import React, { useState, useEffect } from 'react';
import apiClient from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { Skeleton } from './ui/skeleton';

const MarketGraph = ({ marketId }) => {
  const { t, language } = useLanguage();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, [marketId]);

  const fetchHistory = async () => {
    try {
      const response = await apiClient.get(`/markets/${marketId}/history`);
      
      // Determine if timeline spans more than 24 hours
      const spansMoreThan24Hours = response.data.length > 1 && 
        (new Date(response.data[response.data.length - 1].timestamp) - new Date(response.data[0].timestamp) > 24 * 60 * 60 * 1000);

      // Format data for chart
      const formatted = response.data.map((item, index) => {
        const dateObj = new Date(item.timestamp);
        let timeStr = "";
        if (index === 0) {
          timeStr = language === 'de' ? 'Start' : 'Start';
        } else {
          timeStr = dateObj.toLocaleTimeString(
            undefined, 
            { hour: '2-digit', minute: '2-digit' }
          );
        }
        
        const dateStr = dateObj.toLocaleDateString(
          undefined,
          { day: '2-digit', month: '2-digit' }
        );
        
        return {
          ...item,
          time: timeStr,
          date: dateStr,
          displayLabel: index === 0 ? timeStr : (spansMoreThan24Hours ? `${dateStr} ${timeStr}` : timeStr)
        };
      });
      setHistory(formatted);
    } catch (error) {
      console.error('Failed to fetch market history:', error);
    } finally {
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-card/90 backdrop-blur-md border border-border rounded-xl p-3 shadow-2xl animate-in fade-in duration-200">
          <p className="text-xs text-muted-foreground font-sans font-medium mb-2">{data.date} {data.time}</p>
          <div className="space-y-2">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-green-400 font-sans font-bold">
                {t('yes') || 'JA'}
              </p>
              <p className="text-base font-mono font-bold text-green-400">
                {data.yes_percent}% <span className="text-xs text-muted-foreground font-sans">({data.yes_pool.toLocaleString()} {t('coins')})</span>
              </p>
            </div>
            <div className="pt-1.5 border-t border-border mt-1">
              <p className="text-[10px] uppercase tracking-wider text-red-400 font-sans font-bold">
                {t('no') || 'NEIN'}
              </p>
              <p className="text-base font-mono font-bold text-red-400">
                {data.no_percent}% <span className="text-xs text-muted-foreground font-sans">({data.no_pool.toLocaleString()} {t('coins')})</span>
              </p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return <Skeleton className="h-64 rounded-xl" />;
  }

  if (history.length <= 1) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground text-xs italic">
        {language === 'de' ? 'Noch keine Wetten für den Verlauf' : 'No betting history yet'}
      </div>
    );
  }

  // Determine trend color (green if YES went up, red if YES went down)
  const startYes = history.length > 0 ? history[0].yes_percent : 50;
  const currentYes = history.length > 0 ? history[history.length - 1].yes_percent : 50;
  const isUp = currentYes >= startYes;
  const themeColor = isUp ? "#22c55e" : "#ef4444";

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={history} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
        <defs>
          <linearGradient id="colorYes" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.15}/>
            <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
          </linearGradient>
          <linearGradient id="colorNo" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#ef4444" stopOpacity={0.15}/>
            <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
        <XAxis 
          dataKey="displayLabel" 
          stroke="hsl(var(--muted-foreground))" 
          fontSize={10}
          tickLine={false}
          axisLine={false}
          minTickGap={40}
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        />
        <YAxis 
          stroke="hsl(var(--muted-foreground))" 
          fontSize={10}
          tickLine={false}
          axisLine={false}
          domain={[0, 100]}
          tickFormatter={(value) => `${value}%`}
          ticks={[0, 25, 50, 75, 100]}
          style={{ fontFamily: 'JetBrains Mono, monospace' }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend 
          verticalAlign="top"
          height={36}
          formatter={(value) => (
            <span className={value === 'yes_percent' ? 'text-green-400 font-bold text-xs font-sans' : 'text-red-400 font-bold text-xs font-sans'}>
              {value === 'yes_percent' ? (language === 'de' ? 'JA' : 'YES') : (language === 'de' ? 'NEIN' : 'NO')}
            </span>
          )}
        />
        <Area
          type="linear"
          dataKey="yes_percent"
          name="yes_percent"
          stroke="#22c55e"
          strokeWidth={2}
          fill="url(#colorYes)"
          animationDuration={800}
        />
        <Area
          type="linear"
          dataKey="no_percent"
          name="no_percent"
          stroke="#ef4444"
          strokeWidth={2}
          fill="url(#colorNo)"
          animationDuration={800}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default MarketGraph;
